import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { GitBranch, CheckCircle2, XCircle, ShieldAlert, History, Eye, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'pending',  label: 'Awaiting Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const FieldDiff = ({ before, after }) => {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) {
    return <p className="text-[#A3A3A3] text-xs">No fields to diff.</p>;
  }
  return (
    <div className="space-y-2" data-testid="edit-diff">
      {keys.map((k) => {
        const b = before ? before[k] : undefined;
        const a = after  ? after[k]  : undefined;
        const changed = JSON.stringify(b) !== JSON.stringify(a);
        if (!changed && a === undefined) return null;
        return (
          <div key={k} className="text-xs grid grid-cols-[180px_1fr_1fr] gap-2 items-start">
            <div className="text-[#D4AF37] font-mono pt-1">{k}</div>
            <div className="bg-[#0A0A0A] border border-[#262626] rounded p-2 text-[#A3A3A3] line-through whitespace-pre-wrap break-words">
              {b === undefined ? <span className="opacity-50">(new)</span> : (typeof b === 'object' ? JSON.stringify(b, null, 2) : String(b))}
            </div>
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded p-2 text-white whitespace-pre-wrap break-words">
              {a === undefined ? <span className="opacity-50">(unchanged)</span> : (typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const cfg = {
    pending:  { c: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30', l: 'Pending' },
    approved: { c: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', l: 'Approved' },
    rejected: { c: 'bg-red-500/15 text-red-300 border-red-500/30', l: 'Rejected' },
  }[status] || { c: 'bg-[#262626] text-[#A3A3A3]', l: status };
  return <Badge className={`${cfg.c} border`}>{cfg.l}</Badge>;
};

const AdminChangeApprovals = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);     // expanded row with current_record
  const [decisionNote, setDecisionNote] = useState('');
  const [busyId, setBusyId] = useState(null);

  const canOverride = user?.role === 'super_admin';

  const fetchAll = async (status = tab) => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        axios.get(`${API}/admin/edits?status=${status}`, { withCredentials: true }),
        axios.get(`${API}/admin/edits/stats`, { withCredentials: true }),
      ]);
      setItems(list.items || []);
      setStats(s || stats);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load change queue');
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(tab); /* eslint-disable-next-line */ }, [tab]);

  const openDetail = async (edit) => {
    setDetail({ ...edit, current_record: null, loading: true });
    try {
      const { data } = await axios.get(`${API}/admin/edits/${edit.id}`, { withCredentials: true });
      setDetail({ ...data, loading: false });
    } catch (e) {
      toast.error('Could not load edit details');
      setDetail(null);
    }
  };

  const submitDecision = async (action) => {
    if (!detail) return;
    setBusyId(detail.id);
    try {
      await axios.post(`${API}/admin/edits/${detail.id}/decision`, { action, note: decisionNote }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Change approved' : 'Change rejected');
      setDetail(null);
      setDecisionNote('');
      fetchAll(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || e.response?.data?.detail || 'Decision failed');
    }
    setBusyId(null);
  };

  const submitForceApprove = async () => {
    if (!detail) return;
    if (!window.confirm('Force-approve this change as super-admin? This bypasses normal review.')) return;
    setBusyId(detail.id);
    try {
      await axios.post(`${API}/admin/edits/${detail.id}/force-approve`, { action: 'approve', note: decisionNote || 'Force-approved' }, { withCredentials: true });
      toast.success('Force-approved & published');
      setDetail(null);
      setDecisionNote('');
      fetchAll(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || e.response?.data?.detail || 'Force-approve failed');
    }
    setBusyId(null);
  };

  const tabPending = stats.pending || 0;

  return (
    <div className="p-6 space-y-6" data-testid="admin-change-approvals">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-[#D4AF37]" />
            Change Approvals
          </h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Review property, asset, knowledge-base & employee edits submitted by your team.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Badge className="bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">{stats.pending} pending</Badge>
          <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{stats.approved} approved</Badge>
          <Badge className="bg-red-500/15 text-red-300 border border-red-500/30">{stats.rejected} rejected</Badge>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#141414] border border-[#262626]">
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`change-tab-${t.key}`} className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              {t.label}
              {t.key === 'pending' && tabPending > 0 && (
                <span className="ml-2 bg-yellow-500 text-[#0A0A0A] rounded-full px-2 text-xs">{tabPending}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        {loading ? (
          <div className="p-8 text-[#A3A3A3] text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]" data-testid="approvals-empty">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[#D4AF37]" />
            <p className="text-base">All caught up — no {tab} changes.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#262626]">
            {items.map((e) => (
              <li key={e.id} data-testid={`edit-row-${e.id}`} className="p-4 flex items-start gap-4 hover:bg-[#1a1a1a] transition">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">{e.collection_key}</Badge>
                    <StatusPill status={e.status} />
                    {e.forced && <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30">FORCED</Badge>}
                  </div>
                  <p className="text-white text-sm mt-2">
                    <span className="font-medium">{e.submitted_by_name}</span>
                    <span className="text-[#A3A3A3]"> · {e.submitted_by_role} · {new Date(e.created_at).toLocaleString()}</span>
                  </p>
                  <p className="text-[#A3A3A3] text-xs mt-1 truncate">Record <span className="font-mono text-[#D4AF37]">{e.record_id.slice(0, 12)}…</span></p>
                  {e.note && <p className="text-white text-xs mt-2 italic">"{e.note}"</p>}
                  {e.decision_note && <p className="text-[#A3A3A3] text-xs mt-1">Reviewer: <span className="text-white">{e.decision_note}</span></p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button size="sm" variant="outline" className="border-[#404040] text-white" data-testid={`view-edit-${e.id}`} onClick={() => openDetail(e)}>
                    <Eye className="w-4 h-4 mr-1" /> Review
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Review dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDecisionNote(''); } }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#D4AF37]" /> Review change</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              {detail?.collection_key} · submitted by {detail?.submitted_by_name} ({detail?.submitted_by_role})
            </DialogDescription>
          </DialogHeader>

          {detail?.loading ? (
            <div className="p-6 text-[#A3A3A3] text-center">Loading…</div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="text-xs text-[#A3A3A3]">
                <p>Record: <span className="font-mono text-[#D4AF37]">{detail.record_id}</span></p>
                <p>Submitted: {new Date(detail.created_at).toLocaleString()}</p>
                {detail.note && <p className="mt-1 italic text-white">"{detail.note}"</p>}
              </div>

              <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Proposed changes (diff vs current)</p>
                <FieldDiff before={detail.current_record} after={detail.proposed_changes} />
              </div>

              {detail.status === 'pending' && (
                <>
                  <div>
                    <Label className="text-white">Decision note <span className="text-[#525252] text-xs">(visible to submitter)</span></Label>
                    <Textarea data-testid="decision-note" rows={2} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white" />
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" className="border-[#404040] text-white" onClick={() => setDetail(null)} disabled={!!busyId}>Cancel</Button>
                    <Button data-testid="reject-edit" onClick={() => submitDecision('reject')} disabled={!!busyId} className="bg-red-600 text-white hover:bg-red-700"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
                    <Button data-testid="approve-edit" onClick={() => submitDecision('approve')} disabled={!!busyId} className="bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve & Publish</Button>
                  </div>
                </>
              )}

              {detail.status === 'rejected' && canOverride && (
                <div className="border border-purple-500/30 rounded-md p-3 bg-purple-500/5">
                  <p className="text-purple-300 text-sm flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Super-admin override</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">You can force-approve this rejected change. The decision will be audit-logged as a forced publish.</p>
                  <Textarea data-testid="force-note" rows={2} placeholder="Override reason" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} className="mt-2 bg-[#0A0A0A] border-[#262626] text-white" />
                  <div className="flex justify-end mt-2">
                    <Button data-testid="force-approve" onClick={submitForceApprove} disabled={!!busyId} className="bg-purple-600 text-white hover:bg-purple-700"><AlertTriangle className="w-4 h-4 mr-1" /> Force-Approve</Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChangeApprovals;
